import { useState } from "react";
import { Formik, Field, Form, FieldProps } from "formik";
import Button from "@mui/material/Button";
import LoadingButton from "@mui/lab/LoadingButton";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid";
import PasswordTextField from "../PasswordTextField";
import { validationSchema } from "./changePasswordValidationSchema";

interface Props {
  open: boolean;
  onSubmit: ({
    currentPassword,
    newPassword,
  }: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<boolean>;
  onClose: (_: any, reason?: "escapeKeyDown" | "backdropClick") => void;
}

function ChangePasswordDialog({ open, onClose, onSubmit }: Props) {
  const [loading, setLoading] = useState(false);

  const fields: { [key: string]: string | boolean } = {
    currentPassword: "",
    newPassword: "",
    repeatPassword: "",
  };

  const inputProps: { [key: string]: any } = {
    currentPassword: {
      label: "Current Password",
      placeholder: "Enter your current password",
      type: "text",
      Component: PasswordTextField,
    },
    newPassword: {
      label: "New password",
      placeholder: "Enter your new password",
      type: "text",
      Component: PasswordTextField,
    },
    repeatPassword: {
      label: "Repeat password",
      placeholder: "Repeat your new password",
      type: "text",
      Component: PasswordTextField,
    },
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={onClose}
        disableEscapeKeyDown
        disableEnforceFocus
        fullWidth
        maxWidth="sm"
      >
        <Formik
          initialValues={{ ...fields }}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            setLoading(true);
            await onSubmit({
              currentPassword: values.currentPassword as string,
              newPassword: values.newPassword as string,
            });
            setSubmitting(false);
            setLoading(false);
          }}
        >
          <Form noValidate>
            <DialogTitle>Change Password</DialogTitle>
            <DialogContent>
              <Grid container spacing={2} mt={0.1}>
                {Object.keys(fields).map((fieldName) => {
                  if (!inputProps[fieldName]) return null;

                  const { Component, type, name, grid, evalProps, ...props } =
                    inputProps[fieldName];

                  return (
                    <Grid item xs={12} key={fieldName} {...grid}>
                      <Field type={type} name={fieldName}>
                        {({ field, meta, form }: FieldProps) => {
                          return (
                            <Component
                              {...field}
                              {...props}
                              {...(evalProps
                                ? evalProps({ field, meta, form })
                                : {})}
                              type={type}
                              disabled={loading || form.isSubmitting}
                              required
                              fullWidth={type === "text"}
                              error={meta.touched && !!meta.error}
                              helperText={
                                meta.touched && !!meta.error ? meta.error : null
                              }
                            />
                          );
                        }}
                      </Field>
                    </Grid>
                  );
                })}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button color="primary" onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton
                loading={loading}
                loadingPosition="end"
                endIcon={<SaveIcon />}
                variant="contained"
                type="submit"
              >
                Update
              </LoadingButton>
            </DialogActions>
          </Form>
        </Formik>
      </Dialog>
    </div>
  );
}

export default ChangePasswordDialog;
